import { useRef, useState } from "react";
import { X, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, photoUrl, formatApiError } from "../api";
import { EVENT_TYPES_FOR_FORM, trUpper } from "../lib/labels";
import { autoVehicleImage } from "../lib/vehicleImages";
import { ocrVinFromImageFile } from "../lib/vinOcr";
import { inputCls, labelCls } from "./detail/SectionCard";
import { useAuth } from "../context/AuthContext";
import { BodyTypeField, TrimField, BrandField, ModelField, YearField, EngineField, FuelField, TransmissionField, ColorField } from "./VehicleSpecFields";
import { VEHICLE_TYPES } from "../lib/vehicleCatalog";

function Modal({ title, onClose, children, testid }) {
  return (
    <div data-testid={`${testid}-backdrop`} className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div data-testid={testid} className="w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl bg-slate-900 border border-white/10 shadow-2xl p-6 sm:p-8"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-white">{title}</h2>
          <button data-testid={`${testid}-close`} onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function VehicleFormModal({ vehicleForm, setVehicleForm, uploading, fileRef, onUploadPhotos, onSubmit, onClose }) {
  const setField = (k, val) => setVehicleForm((f) => ({ ...f, data: { ...f.data, [k]: val } }));
  const cardPhotoRef = useRef(null);
  const [photoBusy, setPhotoBusy] = useState(false);
  const pickCardPhoto = async (e) => {
    const f = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!f) return;
    if (!["image/jpeg", "image/png"].includes(f.type)) { toast.error("Sadece JPG veya PNG yükleyebilirsiniz"); return; }
    if (f.size > 5 * 1024 * 1024) { toast.error("Fotoğraf 5 MB'ı aşamaz"); return; }
    setPhotoBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/upload", fd);
      setField("photo", data.path);
      toast.success("Fotoğraf yüklendi");
    } catch (err) { toast.error(formatApiError(err)); }
    setPhotoBusy(false);
  };
  const ruhsatRef = useRef(null);
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [ruhsatBusy, setRuhsatBusy] = useState(false);
  const [ruhsatOcr, setRuhsatOcr] = useState(null);
  const pickRuhsat = async (e) => {
    const f = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!f) return;
    if (!["image/jpeg", "image/png"].includes(f.type)) { toast.error("Ruhsat için sadece JPG veya PNG yükleyebilirsiniz"); return; }
    if (f.size > 5 * 1024 * 1024) { toast.error("Fotoğraf 5 MB'ı aşamaz"); return; }
    setRuhsatBusy(true);
    setRuhsatOcr(null);
    setField("ruhsat", "");
    setField("ocr_vin", "");
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/upload", fd);
      setField("ruhsat", data.path);
      setRuhsatOcr({ kind: "reading", text: "Ruhsat üzerindeki şasi numarası okunuyor..." });
      try {
        const found = await ocrVinFromImageFile(f);
        setField("ocr_vin", found || "");
        const entered = (vehicleForm.data.vin || "").trim().toUpperCase().replace(/\s/g, "");
        if (!found) setRuhsatOcr({ kind: "warn", text: "Şasi numarası okunamadı; kayıt yönetici incelemesine gider." });
        else if (entered && entered === found) setRuhsatOcr({ kind: "ok", text: "Şasi numarası ruhsatla eşleşti — doğrulandı." });
        else if (entered) setRuhsatOcr({ kind: "warn", text: `Ruhsattaki şasi (${found}) girilen numarayla eşleşmiyor; kayıt yönetici incelemesine gider.` });
        else setRuhsatOcr({ kind: "ok", text: `Şasi numarası ruhsattan okundu: ${found}` });
      } catch { setRuhsatOcr({ kind: "warn", text: "Ruhsat okunamadı; kayıt yönetici incelemesine gider." }); }
    } catch (err) { toast.error(formatApiError(err)); }
    setRuhsatBusy(false);
  };
  return (
    <Modal testid="vehicle-form-modal" title={vehicleForm.mode === "add" ? "Araç Ekle" : "Aracı Düzenle"} onClose={onClose}>
      <form onSubmit={onSubmit} className="space-y-4">
        <div>
          <label className={labelCls}>Şasi No / VIN *</label>
          <input data-testid="vehicle-form-vin" required disabled={vehicleForm.mode === "edit"}
            value={vehicleForm.data.vin}
            onChange={(e) => setField("vin", e.target.value.toUpperCase())}
            className={`${inputCls} font-mono-vin tracking-widest disabled:opacity-50`} placeholder="17 haneli şasi numarası" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <BrandField value={vehicleForm.data.brand} testid="vehicle-form-brand" inputCls={inputCls} labelCls={labelCls}
            onChange={(v) => setVehicleForm((f) => ({ ...f, data: { ...f.data, brand: v, model: "", trim: "", engine: "" } }))} />
          <ModelField brand={vehicleForm.data.brand} value={vehicleForm.data.model} testid="vehicle-form-model" inputCls={inputCls} labelCls={labelCls}
            onChange={(v) => setField("model", v)} />
          <YearField value={vehicleForm.data.year} testid="vehicle-form-year" inputCls={inputCls} labelCls={labelCls}
            onChange={(v) => setField("year", v)} />
          <div>
            <label className={labelCls}>Plaka</label>
            <input data-testid="vehicle-form-plate" value={vehicleForm.data.plate}
              onChange={(e) => setField("plate", trUpper(e.target.value))} className={inputCls} placeholder="34 ABC 123" />
          </div>
          <div>
            <label className={labelCls}>Kilometre</label>
            <input data-testid="vehicle-form-km" type="number" min="0" value={vehicleForm.data.km}
              onChange={(e) => setField("km", e.target.value)} className={inputCls} />
          </div>
          <EngineField brand={vehicleForm.data.brand} value={vehicleForm.data.engine} testid="vehicle-form-engine" inputCls={inputCls} labelCls={labelCls}
            onChange={(v) => setField("engine", v)} />
          <FuelField value={vehicleForm.data.fuel} testid="vehicle-form-fuel" inputCls={inputCls} labelCls={labelCls}
            onChange={(v) => setField("fuel", v)} />
          <TransmissionField value={vehicleForm.data.transmission} testid="vehicle-form-transmission" inputCls={inputCls} labelCls={labelCls}
            onChange={(v) => setField("transmission", v)} />
          <ColorField value={vehicleForm.data.color} testid="vehicle-form-color" inputCls={inputCls} labelCls={labelCls}
            onChange={(v) => setField("color", v)} />
          <BodyTypeField value={vehicleForm.data.body_type} onChange={(v) => setField("body_type", v)}
            testid="vehicle-form-body-type" inputCls={inputCls} labelCls={labelCls} />
          <TrimField brand={vehicleForm.data.brand} value={vehicleForm.data.trim} onChange={(v) => setField("trim", v)}
            testid="vehicle-form-trim" inputCls={inputCls} labelCls={labelCls} />
          <div>
            <label className={labelCls}>Araç Türü</label>
            <select data-testid="vehicle-form-vehicle-type" value={vehicleForm.data.vehicle_type || "Otomobil"}
              onChange={(e) => setField("vehicle_type", e.target.value)} className={inputCls}>
              {VEHICLE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
        </div>

        <div>
          <label className={labelCls}>Araç Fotoğrafı (kart üzerinde görünür)</label>
          <input ref={cardPhotoRef} type="file" accept="image/jpeg,image/png" className="hidden"
            data-testid="vehicle-form-photo-input" onChange={pickCardPhoto} />
          <div className="flex items-center gap-3">
            {vehicleForm.data.photo ? (
              <img src={photoUrl(vehicleForm.data.photo)} alt="" className="w-28 h-20 object-cover rounded-lg border border-white/10" />
            ) : (
              <div className="relative w-28 h-20">
                <img src={autoVehicleImage(vehicleForm.data.brand, vehicleForm.data.model)} alt="Örnek araç görseli"
                  data-testid="vehicle-form-auto-photo" className="w-28 h-20 object-cover rounded-lg border border-white/10" />
                <span className="absolute bottom-1 left-1 text-[9px] font-bold px-1.5 py-0.5 rounded bg-slate-950/85 text-slate-300">Örnek</span>
              </div>
            )}
            <button type="button" data-testid="vehicle-form-photo-btn" onClick={() => cardPhotoRef.current?.click()} disabled={photoBusy}
              className="px-4 py-2.5 rounded-xl border border-white/10 text-slate-300 text-xs font-semibold hover:border-cyan-400/50 hover:text-cyan-300 transition-colors disabled:opacity-50">
              {photoBusy ? "Yükleniyor..." : vehicleForm.data.photo ? "Fotoğrafı Değiştir" : "Fotoğraf Ekle"}
            </button>
          </div>
          <p className="text-[11px] text-slate-500 mt-1">JPG veya PNG, en fazla 5 MB.</p>
        </div>

        <p data-testid="vehicle-form-belge-note" className="text-[11px] text-slate-500 rounded-xl border border-white/10 bg-slate-950/40 px-4 py-3">
          Belge tarihlerini (kasko, trafik sigortası, muayene) kayıt sonrasında aracınızın detay sayfasındaki <b>Belgeler</b> bölümünden yönetebilirsiniz.
        </p>
        <div>
          <label className={labelCls}>Araç Fotoğrafları</label>
          <input ref={fileRef} data-testid="vehicle-form-photos" type="file" accept="image/*" multiple className="hidden"
            onChange={async (e) => {
              const paths = await onUploadPhotos([...e.target.files]);
              if (paths.length) setVehicleForm((f) => ({ ...f, data: { ...f.data, photos: [...f.data.photos, ...paths] } }));
              e.target.value = "";
            }} />
          <button type="button" data-testid="vehicle-form-upload-button" onClick={() => fileRef.current?.click()} disabled={uploading}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-dashed border-white/20 text-slate-300 text-sm hover:border-cyan-400/50 hover:text-cyan-300 transition-colors w-full justify-center disabled:opacity-50">
            <Upload className="w-4 h-4" /> {uploading ? "Yükleniyor..." : "Fotoğraf Yükle"}
          </button>
          {vehicleForm.data.photos.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {vehicleForm.data.photos.map((p, i) => (
                <div key={p} className="relative">
                  <img src={photoUrl(p)} alt="" className="w-20 h-14 object-cover rounded-lg border border-white/10" />
                  <button type="button" data-testid={`remove-photo-${i}`}
                    onClick={() => setVehicleForm((f) => ({ ...f, data: { ...f.data, photos: f.data.photos.filter((_, j) => j !== i) } }))}
                    className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center">
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
        {vehicleForm.mode === "add" && (
          <div data-testid="vehicle-form-ruhsat-card">
            <label className={labelCls}>Ruhsat (Şasi Doğrulama) {isAdmin ? "— yönetici için opsiyonel" : "*"}</label>
            <input ref={ruhsatRef} type="file" accept="image/jpeg,image/png" className="hidden"
              data-testid="vehicle-form-ruhsat-input" onChange={pickRuhsat} />
            <div className="flex items-center gap-3 flex-wrap">
              <button type="button" data-testid="vehicle-form-ruhsat-btn" onClick={() => ruhsatRef.current?.click()} disabled={ruhsatBusy}
                className="px-4 py-2.5 rounded-xl border border-white/10 text-slate-300 text-xs font-semibold hover:border-cyan-400/50 hover:text-cyan-300 transition-colors disabled:opacity-50">
                {ruhsatBusy ? "Yükleniyor..." : vehicleForm.data.ruhsat ? "Ruhsatı Değiştir" : "Ruhsat Yükle (Kamera / Galeri)"}
              </button>
              {vehicleForm.data.ruhsat && !ruhsatBusy && (
                <span data-testid="vehicle-form-ruhsat-ok" className="text-xs font-semibold text-emerald-300">Ruhsat yüklendi</span>
              )}
              {ruhsatOcr && (
                <span data-testid="vehicle-form-ruhsat-ocr" className={`text-xs font-semibold leading-snug ${ruhsatOcr.kind === "ok" ? "text-emerald-300" : ruhsatOcr.kind === "reading" ? "text-slate-400" : "text-amber-300"}`}>
                  {ruhsatOcr.text}
                </span>
              )}
            </div>
            <p className="text-[11px] text-slate-500 mt-1">Ruhsattaki şasi numarası, girdiğiniz numarayla karşılaştırılır; eşleşirse araç "Doğrulandı" olur, eşleşmezse veya okunamazsa yönetici incelemesine gider. Ruhsat yüklenmeden kayıt tamamlanamaz.</p>
          </div>
        )}
        <button data-testid="vehicle-form-submit" type="submit"
          disabled={vehicleForm.mode === "add" && !isAdmin && (!vehicleForm.data.ruhsat || ruhsatBusy)}
          className="w-full py-3 rounded-xl bg-cyan-400 text-slate-950 font-bold hover:bg-cyan-300 transition-all disabled:opacity-50">
          {vehicleForm.mode === "add" ? "Aracı Kaydet" : "Değişiklikleri Kaydet"}
        </button>
      </form>
    </Modal>
  );
}

export function GarageRecordModal({ recordForm, setRecordForm, onSubmit, onClose }) {
  const setField = (k, val) => setRecordForm((f) => ({ ...f, data: { ...f.data, [k]: val } }));
  return (
    <Modal testid="record-form-modal" title={recordForm.record ? "Kaydı Düzenle" : "Kayıt Ekle"} onClose={onClose}>
      <form onSubmit={onSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Kayıt Tipi *</label>
            <select data-testid="record-form-type" value={recordForm.data.type}
              onChange={(e) => setField("type", e.target.value)} className={inputCls}>
              {EVENT_TYPES_FOR_FORM.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>Tarih *</label>
            <input data-testid="record-form-date" required type="date" value={recordForm.data.date}
              onChange={(e) => setField("date", e.target.value)} className={inputCls} />
          </div>
        </div>
        <div>
          <label className={labelCls}>Başlık *</label>
          <input data-testid="record-form-title" required value={recordForm.data.title}
            onChange={(e) => setField("title", e.target.value)} className={inputCls} placeholder="Periyodik Bakım" />
        </div>
        <div>
          <label className={labelCls}>Açıklama</label>
          <textarea data-testid="record-form-description" rows={3} value={recordForm.data.description}
            onChange={(e) => setField("description", e.target.value)} className={inputCls} placeholder="Detaylı açıklama..." />
        </div>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className={labelCls}>Kilometre</label>
            <input data-testid="record-form-km" type="number" min="0" value={recordForm.data.km}
              onChange={(e) => setField("km", e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Maliyet (₺)</label>
            <input data-testid="record-form-cost" type="number" min="0" value={recordForm.data.cost}
              onChange={(e) => setField("cost", e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Şiddet</label>
            <select data-testid="record-form-severity" value={recordForm.data.severity}
              onChange={(e) => setField("severity", e.target.value)} className={inputCls}>
              <option value="low">Hafif</option>
              <option value="medium">Orta</option>
              <option value="severe">Ağır</option>
            </select>
          </div>
        </div>
        <button data-testid="record-form-submit" type="submit"
          className="w-full py-3 rounded-xl bg-cyan-400 text-slate-950 font-bold hover:bg-cyan-300 transition-all">
          {recordForm.record ? "Kaydı Güncelle" : "Kaydı Ekle"}
        </button>
      </form>
    </Modal>
  );
}

export function TransferModal({ vehicle, email, setEmail, onSubmit, onClose }) {
  return (
    <Modal testid="transfer-modal" title="Araç Kaydını Devret" onClose={onClose}>
      <p className="text-sm text-slate-400 mb-2">
        <b className="text-slate-200">{vehicle.brand} {vehicle.model}</b> ({vehicle.vin}) aracının tüm kayıtları,
        yeni sahibinin hesabına aktarılacaktır.
      </p>
      <p className="text-sm text-cyan-300/90 mb-5">Yeni sahibe e-posta ile devir daveti gönderilir. Araç, karşı taraf daveti kabul edene kadar sizde kalır; kabul sonrası tüm kayıtlarıyla birlikte yeni sahibine aktarılır.</p>
      <form onSubmit={onSubmit} className="space-y-4">
        <div>
          <label className={labelCls}>Yeni Sahibin E-posta Adresi *</label>
          <input data-testid="transfer-email-input" required type="email" value={email}
            onChange={(e) => setEmail(e.target.value)} className={inputCls} placeholder="yenisahip@eposta.com" />
        </div>
        <button data-testid="transfer-submit-button" type="submit"
          className="w-full py-3 rounded-xl bg-violet-500 text-white font-bold hover:bg-violet-400 transition-all">
          Devri Onayla
        </button>
      </form>
    </Modal>
  );
}
